// Figma: https://www.figma.com/design/iYlbYhvyJjh7X00UAcqCWt/About-page?node-id=13-5301
import { Flex, Image, Text } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useEffect, useRef, useState } from "react";
import { aboutAsset } from "../aboutAssets";

const bracketLeft = aboutAsset("bracket-left.svg");
const bracketRight = aboutAsset("bracket-right.svg");
const togetherSvg = aboutAsset("together.svg");

const gradientShift = keyframes`
  0% { background-position: 0% center; }
  100% { background-position: 300% center; }
`;

export function ManifestoSection() {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = contentRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const revealStyle = (delay: number) => ({
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(24px)",
    transition: `opacity 0.9s ease-out ${delay}ms, transform 0.9s ease-out ${delay}ms`,
  });

  return (
    <Flex
      maxW="960px"
      mx="auto"
      px="24px"
      py="80px"
      align="center"
      justify="center"
      gap={{ base: "16px", md: "57px" }}
    >
      {/* Left bracket (13:5302) */}
      <Image
        src={bracketLeft}
        alt=""
        h="246px"
        w="40px"
        flexShrink={0}
        display={{ base: "none", md: "block" }}
      />

      {/* Content (13:5397) */}
      <Flex
        ref={contentRef}
        direction="column"
        gap="32px"
        maxW="727px"
        textAlign="center"
        align="center"
      >
        {/* Main quote (13:5399) */}
        <Text
          fontSize="24px"
          fontWeight="400"
          color="dune.700"
          lineHeight="1.5"
          sx={revealStyle(0)}
        >
          Comics and manga are global, but the experience around them is
          fragmented. Reading happens in one place. Community in another.
          Discovery somewhere else.
        </Text>

        {/* Bottom tagline (13:5405) */}
        <Flex
          align="center"
          gap="12px"
          wrap="wrap"
          justify="center"
          sx={revealStyle(600)}
        >
          {/* "We're bringing it all" (13:5406) */}
          <Text
            as="span"
            fontSize="32px"
            fontWeight="400"
            lineHeight="1.3"
            sx={{
              background: "linear-gradient(90deg, var(--chakra-colors-dune-700), var(--chakra-colors-dune-dune), var(--chakra-colors-dune-700), var(--chakra-colors-dune-700))",
              backgroundSize: "300% 100%",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              animation: `${gradientShift} 6s linear infinite`,
            }}
          >
            We&rsquo;re bringing it all
          </Text>
          {/* "together" in Mandoul Brush as SVG (13:5407) */}
          <Image
            src={togetherSvg}
            alt="together"
            w="155px"
            h="auto"
            transform="rotate(-2deg)"
          />
        </Flex>
      </Flex>

      {/* Right bracket (13:5408) */}
      <Image
        src={bracketRight}
        alt=""
        h="252px"
        w="51px"
        flexShrink={0}
        display={{ base: "none", md: "block" }}
      />
    </Flex>
  );
}
